import { cn } from "../../../lib/utils"
import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps"
import { useCallback, useEffect, useRef, useState } from "react"
import { RiSearchLine } from "react-icons/ri"
import { Input } from "../input"
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip"
import { Button } from "../button"
import { IoClose } from "react-icons/io5"
import { FaLocationDot } from "react-icons/fa6"
import { logger } from "../../../utils/logger"

const useDebounce = (func: Function, delay: number = 200) => {
	const timer = useRef<NodeJS.Timeout | null>(null)

	return useCallback(
		(...args: any[]) => {
			if (timer.current) clearTimeout(timer.current)
			timer.current = setTimeout(() => func(...args), delay)
		},
		[func, delay],
	)
}

interface AddressDetails {
	address: string
	address_line_1: string
	address_line_2: string
	pincode: string
	state: string
	city: string
	district: string
	country: string
	landmark: string
	region: string
	latitude?: number
	longitude?: number
}

interface GooglePlaceAutocompleteProps {
	value: string
	onValueChange: (value: string) => void
	onPlaceSelect: (values: AddressDetails) => void
	className?: string
	inputClassName?: string
	placeholder?: string
	shouldDisable: boolean
}

const GooglePlaceAutocomplete = ({
	value,
	onValueChange,
	onPlaceSelect,
	className = "",
	inputClassName = "",
	placeholder = "Search Address",
	shouldDisable,
}: GooglePlaceAutocompleteProps) => {
	const [results, setResults] = useState<google.maps.places.PlaceResult[]>([])
	const [isVisible, setIsVisible] = useState<boolean>(false)
	const [selectedIndex, setSelectedIndex] = useState<number>(-1)
	const inputRef = useRef<HTMLInputElement>(null)
	const wrapperRef = useRef<HTMLDivElement>(null)

	const places = useMapsLibrary("places")

	// Fetch Place Details
	const getPlaceDetailsById = async (placeId: string) => {
		return new Promise<google.maps.places.PlaceResult | null>(
			(resolve, reject) => {
				if (!places || !placeId) {
					reject(
						new Error(
							"Google Places library not loaded or placeId missing",
						),
					)
					return
				}
				const service = new google.maps.places.PlacesService(
					document.createElement("div"),
				)
				const request = {
					placeId,
					fields: [
						"formatted_address",
						"name",
						"address_components",
						"geometry",
					],
				}
				service.getDetails(request, (place, status) => {
					status === google.maps.places.PlacesServiceStatus.OK &&
					place
						? resolve(place)
						: reject(
								new Error(
									`Failed to fetch place details: ${status}`,
								),
							)
				})
			},
		)
	}

	// Fetch Search Results
	const fetchSearchResults = useDebounce((query: string) => {
		if (!places || !query.trim()) {
			setResults([])
			return
		}
		const service = new google.maps.places.PlacesService(
			document.createElement("div"),
		)
		const request = {
			query,
			fields: [
				"formatted_address",
				"name",
				"address_components",
				"geometry",
			],
		}

		service.textSearch(request, async (results, status) => {
			if (
				status === google.maps.places.PlacesServiceStatus.OK &&
				results
			) {
				try {
					const detailedResults = await Promise.all(
						results.map(result =>
							getPlaceDetailsById(result.place_id ?? "").catch(
								() => null,
							),
						),
					)
					setResults(
						detailedResults.filter(
							Boolean,
						) as google.maps.places.PlaceResult[],
					)
					setIsVisible(true)
				} catch (error) {
					logger.error(
						"Error fetching detailed place results:",
						error,
					)
					setResults([])
					setIsVisible(false)
				}
			} else {
				setResults([])
				setIsVisible(false)
			}
		})
	})

	// Effect: Fetch Search Results
	useEffect(() => fetchSearchResults(value), [value])

	// Effect: Handle Click Outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node) &&
				setIsVisible(false)
		}
		document.addEventListener("mousedown", handleClickOutside)
		return () =>
			document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	// Effect: Handle Key Down
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (!isVisible) return

			switch (event.key) {
				case "ArrowDown":
					setSelectedIndex(prevIndex =>
						prevIndex < results.length - 1 ? prevIndex + 1 : 0,
					)
					break
				case "ArrowUp":
					setSelectedIndex(prevIndex =>
						prevIndex > 0 ? prevIndex - 1 : results.length - 1,
					)
					break
				case "Enter":
					if (selectedIndex >= 0 && selectedIndex < results.length) {
						handlePlaceSelect(results[selectedIndex])
					}
					break
				default:
					break
			}
		}

		document.addEventListener("keydown", handleKeyDown)
		return () => document.removeEventListener("keydown", handleKeyDown)
	}, [isVisible, results, selectedIndex])

	// Extract Address Components
	const extractAddressDetails = (
		place: google.maps.places.PlaceResult,
	): AddressDetails => {
		const addressComponents = (type: string) =>
			place.address_components?.find(component =>
				component.types.includes(type),
			)?.long_name || ""

		const addressLineOne = [
			addressComponents("street_number"),
			addressComponents("route"),
			addressComponents("premise"),
			addressComponents("sublocality_level_2"),
		]
			.filter(Boolean)
			.join(", ")

		const addressLineTwo = [
			addressComponents("sublocality_level_1"),
			addressComponents("sublocality_level_3"),
			addressComponents("neighborhood"),
		]
			.filter(Boolean)
			.join(", ")

		return {
			address: `${place.name}, ${place.formatted_address}`,
			address_line_1: addressLineOne,
			address_line_2: addressLineTwo,
			pincode: addressComponents("postal_code"),
			state: addressComponents("administrative_area_level_1"),
			city: addressComponents("locality"),
			district: addressComponents("administrative_area_level_3"),
			country: addressComponents("country"),
			landmark: addressComponents("landmark"),
			region: "",
			latitude: place.geometry?.location?.lat(),
			longitude: place.geometry?.location?.lng(),
		}
	}

	// Handle Place Select
	const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
		onPlaceSelect(extractAddressDetails(place))
		inputRef.current?.blur()
		setIsVisible(false)
	}

	return (
		<div
			ref={wrapperRef}
			className={cn("relative w-full", className)}
			onClick={e => e.stopPropagation()}
		>
			{!places ? (
				<div>Loading places library...</div>
			) : (
				<>
					<div className="absolute left-3.5 top-4 text-slate-500">
						<RiSearchLine className="size-4" />
					</div>
					<Input
						disabled={shouldDisable}
						ref={inputRef}
						value={value}
						onChange={e => onValueChange(e.target.value)}
						placeholder={placeholder}
						className={cn("w-full px-10", inputClassName)}
					/>
					{value && (
						<Tooltip>
							<TooltipContent>Clear Search</TooltipContent>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="absolute right-1 top-1"
									type="button"
									onClick={() => {
										onValueChange("")
										setIsVisible(false)
									}}
								>
									<IoClose className="h-4 w-4 text-slate-600" />
								</Button>
							</TooltipTrigger>
						</Tooltip>
					)}
					{isVisible && value && (
						<div className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
							{results.length ? (
								results.slice(0, 5).map((result, index) => (
									<button
										type="button"
										key={result.place_id}
										className={cn(
											"group flex w-full max-w-full cursor-pointer items-center gap-2 overflow-hidden p-2 text-xs font-normal text-slate-900 hover:bg-gray-100",
											{
												"bg-gray-100":
													index === selectedIndex,
											},
										)}
										onClick={() =>
											handlePlaceSelect(result)
										}
									>
										<FaLocationDot className="h-5 w-5 text-gray-400 group-hover:text-red-500" />
										<span className="truncate">
											{`${result.name}${result.formatted_address ? `, ${result.formatted_address}` : ""}`}
										</span>
									</button>
								))
							) : (
								<div className="p-2 text-gray-500">
									No results found
								</div>
							)}
						</div>
					)}
				</>
			)}
		</div>
	)
}

const GooglePlaceAutocompleteWrapper = (
	props: GooglePlaceAutocompleteProps,
) => {
	const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ""
	return (
		<APIProvider apiKey={API_KEY}>
			<GooglePlaceAutocomplete {...props} />
		</APIProvider>
	)
}

export default GooglePlaceAutocompleteWrapper
